import { FC } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import { IStationInfo, useSystemParams } from "@ryde/shared";

import { FramerTooltip } from "../../../tooltips";
import { getRideStationStopIndicatorState } from "./ride-station-stop-indicator.utils";
import { RideStationStopIndicatorStyled } from "./ride-stations.styled";

interface IRideStationStopIndicator {
  station: IStationInfo | undefined;
}

/**
 * per-station "stopped at station" indication in the ride edit panel. Three states:
 * - green  = vehicle stopped inside the station perimeter (station.stationStopStart set)
 * - orange = vehicle entered the perimeter (station.realArrivalTime set) but never stopped there
 * - gray   = vehicle never reached the station perimeter
 * Only rendered when the company enables the `stoppedAtStationRideEditIndication` param.
 */
export const RideStationStopIndicator: FC<IRideStationStopIndicator> = ({
  station,
}) => {
  const { t } = useTranslation();
  const { stoppedAtStationRideEditIndication } = useSystemParams();

  if (!stoppedAtStationRideEditIndication) {
    return null;
  }

  const hasStopped = Boolean(station?.stationStopStart);
  const hasPassedWithoutStopping =
    !hasStopped && Boolean(station?.realArrivalTime);

  const { className, tooltip } = getRideStationStopIndicatorState({
    hasPassedWithoutStopping,
    hasStopped,
    t,
  });

  return (
    <RideStationStopIndicatorStyled>
      <FramerTooltip content={tooltip}>
        <SVG_MUI.StopAtStation4SVG className={className} />
      </FramerTooltip>
    </RideStationStopIndicatorStyled>
  );
};
